import { useEffect, useRef, useState } from "react";
import { LOCALES, useLocale } from "@/lib/locale";

/**
 * Elegant compact language switcher — IT / EN / ES / PT.
 * Shows the current locale code; click opens a refined dropdown.
 * Styled to match the dark/gold aesthetic.
 */
export default function LanguageSwitcher({ compact = false }) {
    const { locale, setLocale, t } = useLocale();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener("mousedown", close);
        return () => window.removeEventListener("mousedown", close);
    }, []);

    const current = LOCALES.find((l) => l.code === locale) || LOCALES[1];

    return (
        <div ref={ref} className="relative" data-testid="language-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={t("language.pick")}
                data-testid="language-switcher-button"
                className={`inline-flex items-center gap-1.5 border border-ink-border hover:border-ink-gold text-ink-textMid hover:text-ink-gold transition-colors rounded-sm text-xs tracking-widest uppercase ${
                    compact ? "px-2.5 py-1.5" : "px-3 py-2"
                }`}
            >
                <span className="font-semibold">{current.label}</span>
                <svg width="10" height="10" viewBox="0 0 10 10" className="opacity-70" aria-hidden>
                    <path d="M1 3 L5 7 L9 3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {open && (
                <ul
                    role="listbox"
                    className="absolute right-0 mt-2 min-w-[160px] bg-ink-surfaceUp border border-ink-border rounded-sm shadow-[0_12px_40px_-16px_rgba(0,0,0,0.7)] z-30 py-1 animate-fade-in"
                    data-testid="language-switcher-menu"
                >
                    {LOCALES.map((l) => (
                        <li key={l.code}>
                            <button
                                type="button"
                                role="option"
                                aria-selected={l.code === locale}
                                onClick={() => { setLocale(l.code); setOpen(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left ${
                                    l.code === locale
                                        ? "text-ink-gold bg-ink-surface"
                                        : "text-ink-textMid hover:text-ink-textHigh hover:bg-ink-surface/60"
                                }`}
                                data-testid={`language-option-${l.code}`}
                            >
                                <span className="font-semibold w-6">{l.label}</span>
                                <span className="font-serif italic">{l.name}</span>
                                {l.code === locale && <span className="ml-auto text-ink-gold">·</span>}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
